import { useEffect, useState } from "react";
import { api, apiError, money, fileUrl } from "@/lib/api";
import { Loader, EmptyState, StatusBadge, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Truck, FileDown, CheckCircle2, PackageCheck } from "lucide-react";

const FILTERS = ["All", "CONFIRMED", "PROCESSING", "DISPATCHED", "DELIVERED"];

export default function Orders() {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("All");
  const [dispatchOrder, setDispatchOrder] = useState(null);
  const [d, setD] = useState({ courier: "", tracking_id: "", tracking_url: "", expected_delivery: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/manufacturer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);

  async function act(id, action, body) {
    setBusy(true);
    try { await api.post(`/manufacturer/orders/${id}/${action}`, body || {}); toast.success("Order updated"); setDispatchOrder(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }
  async function submitDispatch() {
    if (!d.courier || !d.tracking_id) { toast.error("Courier and tracking ID required"); return; }
    await act(dispatchOrder.id, "dispatch", d);
  }

  if (!orders) return <Loader />;
  const filtered = filter === "All" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-semibold text-forest">Orders</h1>
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-card">{FILTERS.map((f) => <TabsTrigger key={f} value={f} data-testid={`ofilter-${f.toLowerCase()}`} className="data-[state=active]:bg-forest data-[state=active]:text-white">{f}</TabsTrigger>)}</TabsList>
      </Tabs>

      {filtered.length === 0 ? <EmptyState title="No orders here" hint="Paid orders awaiting your action will appear here." icon={ClipboardList} /> : (
        <div className="space-y-3">
          {filtered.map((o) => (
            <div key={o.id} data-testid={`morder-${o.id}`} className="rounded-xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="num text-sm font-semibold text-forest">{o.order_number}</p>
                  <p className="text-xs text-ink/50">{o.retailer_name} · {o.delivery_address.city}, {o.delivery_address.state} · {o.created_at.slice(0, 10)}</p>
                  <div className="mt-2 text-sm text-ink/70">{o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
                </div>
                <div className="text-right">
                  <Money value={o.total} className="text-lg text-forest" />
                  <div className="mt-1"><StatusBadge status={o.status} /></div>
                  <div className="mt-1 text-xs text-ink/50">Settlement: <span className="num text-forest">{money(o.finance.settlement_amount)}</span></div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {o.status === "CONFIRMED" && <Button data-testid={`confirm-${o.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={() => act(o.id, "confirm")}><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Confirm & Process</Button>}
                {o.status === "PROCESSING" && <Button data-testid={`dispatch-${o.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90" onClick={() => { setDispatchOrder(o); setD({ courier: "", tracking_id: "", tracking_url: "", expected_delivery: "" }); }}><Truck className="mr-1.5 h-3.5 w-3.5" /> Dispatch</Button>}
                {o.status === "DISPATCHED" && <Button data-testid={`deliver-${o.id}`} size="sm" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={() => act(o.id, "deliver")}><PackageCheck className="mr-1.5 h-3.5 w-3.5" /> Mark Delivered</Button>}
                {o.invoice_id && <Button size="sm" variant="outline" className="border-forest text-forest hover:bg-psage" onClick={() => window.open(fileUrl(`/api/invoices/${o.invoice_id}/pdf`) + `?t=${localStorage.getItem("script_token")}`, "_blank")}><FileDown className="mr-1.5 h-3.5 w-3.5" /> Invoice</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dispatchOrder} onOpenChange={(v) => !v && setDispatchOrder(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Dispatch {dispatchOrder?.order_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Courier name</Label><Input data-testid="disp-courier" value={d.courier} onChange={(e) => setD({ ...d, courier: e.target.value })} /></div>
            <div><Label className="text-xs">Tracking ID</Label><Input data-testid="disp-tracking" value={d.tracking_id} onChange={(e) => setD({ ...d, tracking_id: e.target.value })} /></div>
            <div><Label className="text-xs">Tracking URL (optional)</Label><Input data-testid="disp-url" value={d.tracking_url} onChange={(e) => setD({ ...d, tracking_url: e.target.value })} /></div>
            <div><Label className="text-xs">Expected delivery</Label><Input data-testid="disp-eta" type="date" value={d.expected_delivery} onChange={(e) => setD({ ...d, expected_delivery: e.target.value })} /></div>
          </div>
          <DialogFooter><Button data-testid="disp-submit" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={submitDispatch}>Confirm Dispatch</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
